// Web CRM (Business plane) — Team Collaboration (Voice/Video calls, staff roster, staff chat)
// STATIC UI CHROME ONLY. Split out of translations.js for file size; merged into the same flat
// per-language lookup at load time (see translations.js). Same rules as the rest of the i18n
// layer: English is the default AND the fallback, and these keys are NEVER used for dynamic/
// backend/user content (staff/customer names, chat message bodies, ticket text, call ids, live
// call/presence status enums). Interpolated tokens ({name}, {count}) are dynamic — never translated.
//
// Reuse existing common.* keys for shared verbs/labels (Cancel, Retry, Offline, etc.) — those are
// NOT redefined here. Everything Team-Collaboration-specific is prefixed `tc.`.
//
// Every language MUST expose the identical key set. Missing keys fall back to English via t().

const teamCollabTranslations = {
  en: {
    // Quick Call dialog + tabs
    'tc.quickCall.desc': "Choose how you'd like to make your call",
    'tc.tab.dialpad': 'Dialpad',
    'tc.tab.recent': 'Recent',
    'tc.tab.contacts': 'Contacts',
    'tc.tab.activeComm': 'Active Communication',
    'tc.tab.analytics': 'Analytics',
    'tc.dialpad.phonePlaceholder': 'Enter staff phone number',
    'tc.dialpad.help': 'Staff-to-staff only — rings a tenant staff member by phone. External (PSTN) dialing is not supported.',
    'tc.clear': 'Clear',
    'tc.call': 'Call',
    'tc.noRecentCalls': 'No recent staff calls yet.',
    'tc.noOtherStaffOnline': 'No other staff currently online.',
    'tc.voiceCall': 'Voice call',
    'tc.videoCall': 'Video call',
    'tc.selectedLabel': 'Selected: {name}',
    // Statistic cards
    'tc.stat.totalCalls': 'Total Calls',
    'tc.stat.successful': 'Successful',
    'tc.stat.successRate': 'Success Rate',
    'tc.stat.onlineNow': 'Online Now',
    'tc.stat.completedCalls': 'Completed Calls',
    // Call history labels
    'tc.teamCallHistory': 'Team Call History',
    'tc.myCallHistory': 'My Call History',
    'tc.history.supervisorDesc': 'You are viewing call history for your whole team (supervisory access).',
    'tc.history.ownDesc': 'You are viewing only your own call history.',
    'tc.history.loading': 'Loading call history...',
    'tc.history.empty': 'No call history available',
    'tc.history.emptyHint': 'Calls will appear here once you start making them',
    // Quick Actions card
    'tc.quickActions': 'Quick Actions',
    'tc.startVoiceCall': 'Start Voice Call',
    'tc.startVideoCall': 'Start Video Call',
    'tc.startCustomerConversation': 'Start Customer Conversation',
    'tc.startAudioConference': 'Start Audio Conference',
    'tc.conf.inProgress': 'Conference in progress',
    'tc.conf.startTitle': 'Start an audio conference with online team members',
    'tc.conf.inProgressTitle': 'A conference is already in progress',
    'tc.conf.manageHint': 'Manage participants in the conference panel.',
    'tc.conf.audioOnlyHint': 'Audio-only. Online teammates are invited; add more from the panel.',
    'tc.refreshStatistics': 'Refresh Statistics',
    'tc.refreshing': 'Refreshing…',
    'tc.lastRefreshed': 'Last refreshed',
    // Analytics
    'tc.callPerformance': 'Call Performance',
    'tc.analytics.empty': 'No analytics data available',
    'tc.connectionStatus': 'Connection Status',
    'tc.webrtcStatus': 'WebRTC Status',
    'tc.onlineUsers': 'Online Users',
    'tc.currentCall': 'Current Call',
    // Team conversation dialog
    'tc.teamConv.title': 'Start Team Conversation',
    'tc.teamConv.desc': 'Select team members and compose a message to start a group conversation',
    'tc.teamConv.selectMembers': 'Select Team Members',
    'tc.teamConv.membersSelected': '{count} member(s) selected',
    'tc.teamConv.message': 'Message',
    'tc.teamConv.messagePlaceholder': 'Type your message to the team...',
    'tc.startConversation': 'Start Conversation',
    'tc.noStaffYet': 'No staff in this business yet.',
    'tc.you': '(You)',
    // Create customer support ticket dialog
    'tc.createTicket.title': 'Create Customer Support Ticket',
    'tc.createTicket.desc': 'Select a customer and one of their conversations, then describe the issue.',
    'tc.createTicket.customerReq': 'Customer *',
    'tc.createTicket.conversationReq': 'Conversation *',
    'tc.createTicket.titleReq': 'Ticket Title *',
    'tc.createTicket.descriptionReq': 'Description *',
    'tc.createTicket.loadingCustomers': 'Loading customers…',
    'tc.createTicket.selectCustomer': 'Select a customer',
    'tc.createTicket.selectCustomerFirst': 'Select a customer first',
    'tc.createTicket.loadingConversations': 'Loading conversations…',
    'tc.createTicket.noConversations': 'No conversations for this customer',
    'tc.createTicket.selectConversation': 'Select a conversation',
    'tc.createTicket.titlePlaceholder': 'Brief description of the issue',
    'tc.createTicket.descriptionPlaceholder': 'Detailed description of the issue or request',
    'tc.createTicket.priority': 'Priority',
    'tc.createTicket.category': 'Category',
    'tc.createTicket.create': 'Create Ticket',
    'tc.createTicket.creating': 'Creating…',
    'tc.priority.low': 'Low',
    'tc.priority.medium': 'Medium',
    'tc.priority.high': 'High',
    'tc.priority.urgent': 'Urgent',
    'tc.category.general': 'General Support',
    'tc.category.technical': 'Technical Issue',
    'tc.category.billing': 'Billing',
    'tc.category.feature': 'Feature Request',
    // Quick / new conversation dialog
    'tc.newConv.title': 'Start New Conversation',
    'tc.newConv.desc': 'Enter customer details to start a new chat conversation',
    'tc.newConv.nameReq': 'Customer Name *',
    'tc.newConv.namePlaceholder': 'Enter customer name',
    'tc.newConv.phoneOptional': 'Phone Number (Optional)',
    'tc.newConv.startChat': 'Start Chat',
    // Toast (explicitly localized)
    'tc.toast.noStaffOnline.title': 'No staff online',
    'tc.toast.noStaffOnline.desc': 'No other staff are currently online.',
    // Online users panel
    'tc.teamMembers': 'Team Members',
    'tc.refreshStaffPresence': 'Refresh staff & presence',
    'tc.loadingStaff': 'Loading staff…',
    'tc.loadStaffError': 'Couldn’t load staff. Tap refresh to retry.',
    'tc.thisIsYou': 'This is you',
    'tc.chat': 'Chat',
    'tc.chatUnread': 'Chat · {count} unread',
    'tc.presenceReconnecting': 'Live presence reconnecting… offline/online may be momentarily stale.',
    'tc.videoCallInProgress': 'Video call in progress...',
    'tc.voiceCallInProgress': 'Voice call in progress...',
    // Staff chat modal
    'tc.chatModal.privateDesc': 'Private staff chat — only you and {name} can see these messages.',
    'tc.chatModal.opening': 'Opening chat…',
    'tc.chatModal.noMessages': 'No messages yet. Say hello.',
    'tc.chatModal.inputPlaceholder': 'Type a message…',
    'tc.chatModal.sending': 'Sending…',
    'tc.chatModal.failed': 'Failed ·',
  },
  es: {
    'tc.quickCall.desc': 'Elija cómo desea realizar su llamada',
    'tc.tab.dialpad': 'Teclado',
    'tc.tab.recent': 'Recientes',
    'tc.tab.contacts': 'Contactos',
    'tc.tab.activeComm': 'Comunicación activa',
    'tc.tab.analytics': 'Analíticas',
    'tc.dialpad.phonePlaceholder': 'Ingrese el número de teléfono del personal',
    'tc.dialpad.help': 'Solo entre personal: llama a un miembro del personal del inquilino por teléfono. No se admite la marcación externa (PSTN).',
    'tc.clear': 'Borrar',
    'tc.call': 'Llamar',
    'tc.noRecentCalls': 'Aún no hay llamadas recientes del personal.',
    'tc.noOtherStaffOnline': 'No hay otro personal en línea actualmente.',
    'tc.voiceCall': 'Llamada de voz',
    'tc.videoCall': 'Videollamada',
    'tc.selectedLabel': 'Seleccionado: {name}',
    'tc.stat.totalCalls': 'Llamadas totales',
    'tc.stat.successful': 'Exitosas',
    'tc.stat.successRate': 'Tasa de éxito',
    'tc.stat.onlineNow': 'En línea ahora',
    'tc.stat.completedCalls': 'Llamadas completadas',
    'tc.teamCallHistory': 'Historial de llamadas del equipo',
    'tc.myCallHistory': 'Mi historial de llamadas',
    'tc.history.supervisorDesc': 'Está viendo el historial de llamadas de todo su equipo (acceso de supervisión).',
    'tc.history.ownDesc': 'Está viendo solo su propio historial de llamadas.',
    'tc.history.loading': 'Cargando historial de llamadas...',
    'tc.history.empty': 'No hay historial de llamadas disponible',
    'tc.history.emptyHint': 'Las llamadas aparecerán aquí una vez que comience a realizarlas',
    'tc.quickActions': 'Acciones rápidas',
    'tc.startVoiceCall': 'Iniciar llamada de voz',
    'tc.startVideoCall': 'Iniciar videollamada',
    'tc.startCustomerConversation': 'Iniciar conversación con el cliente',
    'tc.startAudioConference': 'Iniciar conferencia de audio',
    'tc.conf.inProgress': 'Conferencia en curso',
    'tc.conf.startTitle': 'Iniciar una conferencia de audio con los miembros del equipo en línea',
    'tc.conf.inProgressTitle': 'Ya hay una conferencia en curso',
    'tc.conf.manageHint': 'Gestione los participantes en el panel de la conferencia.',
    'tc.conf.audioOnlyHint': 'Solo audio. Se invita a los compañeros en línea; agregue más desde el panel.',
    'tc.refreshStatistics': 'Actualizar estadísticas',
    'tc.refreshing': 'Actualizando…',
    'tc.lastRefreshed': 'Última actualización',
    'tc.callPerformance': 'Rendimiento de llamadas',
    'tc.analytics.empty': 'No hay datos analíticos disponibles',
    'tc.connectionStatus': 'Estado de la conexión',
    'tc.webrtcStatus': 'Estado de WebRTC',
    'tc.onlineUsers': 'Usuarios en línea',
    'tc.currentCall': 'Llamada actual',
    'tc.teamConv.title': 'Iniciar conversación de equipo',
    'tc.teamConv.desc': 'Seleccione miembros del equipo y redacte un mensaje para iniciar una conversación grupal',
    'tc.teamConv.selectMembers': 'Seleccionar miembros del equipo',
    'tc.teamConv.membersSelected': '{count} miembro(s) seleccionado(s)',
    'tc.teamConv.message': 'Mensaje',
    'tc.teamConv.messagePlaceholder': 'Escriba su mensaje para el equipo...',
    'tc.startConversation': 'Iniciar conversación',
    'tc.noStaffYet': 'Aún no hay personal en esta empresa.',
    'tc.you': '(Usted)',
    'tc.createTicket.title': 'Crear ticket de soporte al cliente',
    'tc.createTicket.desc': 'Seleccione un cliente y una de sus conversaciones, luego describa el problema.',
    'tc.createTicket.customerReq': 'Cliente *',
    'tc.createTicket.conversationReq': 'Conversación *',
    'tc.createTicket.titleReq': 'Título del ticket *',
    'tc.createTicket.descriptionReq': 'Descripción *',
    'tc.createTicket.loadingCustomers': 'Cargando clientes…',
    'tc.createTicket.selectCustomer': 'Seleccione un cliente',
    'tc.createTicket.selectCustomerFirst': 'Seleccione un cliente primero',
    'tc.createTicket.loadingConversations': 'Cargando conversaciones…',
    'tc.createTicket.noConversations': 'No hay conversaciones para este cliente',
    'tc.createTicket.selectConversation': 'Seleccione una conversación',
    'tc.createTicket.titlePlaceholder': 'Breve descripción del problema',
    'tc.createTicket.descriptionPlaceholder': 'Descripción detallada del problema o la solicitud',
    'tc.createTicket.priority': 'Prioridad',
    'tc.createTicket.category': 'Categoría',
    'tc.createTicket.create': 'Crear ticket',
    'tc.createTicket.creating': 'Creando…',
    'tc.priority.low': 'Baja',
    'tc.priority.medium': 'Media',
    'tc.priority.high': 'Alta',
    'tc.priority.urgent': 'Urgente',
    'tc.category.general': 'Soporte general',
    'tc.category.technical': 'Problema técnico',
    'tc.category.billing': 'Facturación',
    'tc.category.feature': 'Solicitud de función',
    'tc.newConv.title': 'Iniciar nueva conversación',
    'tc.newConv.desc': 'Ingrese los datos del cliente para iniciar una nueva conversación de chat',
    'tc.newConv.nameReq': 'Nombre del cliente *',
    'tc.newConv.namePlaceholder': 'Ingrese el nombre del cliente',
    'tc.newConv.phoneOptional': 'Número de teléfono (opcional)',
    'tc.newConv.startChat': 'Iniciar chat',
    'tc.toast.noStaffOnline.title': 'No hay personal en línea',
    'tc.toast.noStaffOnline.desc': 'No hay otro personal en línea actualmente.',
    'tc.teamMembers': 'Miembros del equipo',
    'tc.refreshStaffPresence': 'Actualizar personal y presencia',
    'tc.loadingStaff': 'Cargando personal…',
    'tc.loadStaffError': 'No se pudo cargar el personal. Toque actualizar para reintentar.',
    'tc.thisIsYou': 'Este es usted',
    'tc.chat': 'Chat',
    'tc.chatUnread': 'Chat · {count} sin leer',
    'tc.presenceReconnecting': 'Reconectando presencia en vivo… el estado en línea/desconectado puede estar momentáneamente desactualizado.',
    'tc.videoCallInProgress': 'Videollamada en curso...',
    'tc.voiceCallInProgress': 'Llamada de voz en curso...',
    'tc.chatModal.privateDesc': 'Chat privado del personal: solo usted y {name} pueden ver estos mensajes.',
    'tc.chatModal.opening': 'Abriendo chat…',
    'tc.chatModal.noMessages': 'Aún no hay mensajes. Salude.',
    'tc.chatModal.inputPlaceholder': 'Escriba un mensaje…',
    'tc.chatModal.sending': 'Enviando…',
    'tc.chatModal.failed': 'Error ·',
  },
  fr: {
    'tc.quickCall.desc': 'Choisissez comment vous souhaitez passer votre appel',
    'tc.tab.dialpad': 'Clavier',
    'tc.tab.recent': 'Récents',
    'tc.tab.contacts': 'Contacts',
    'tc.tab.activeComm': 'Communication active',
    'tc.tab.analytics': 'Analytique',
    'tc.dialpad.phonePlaceholder': 'Saisissez le numéro de téléphone du personnel',
    'tc.dialpad.help': 'Entre membres du personnel uniquement — appelle un membre du personnel du locataire par téléphone. La numérotation externe (RTC) n’est pas prise en charge.',
    'tc.clear': 'Effacer',
    'tc.call': 'Appeler',
    'tc.noRecentCalls': 'Aucun appel récent du personnel pour le moment.',
    'tc.noOtherStaffOnline': 'Aucun autre membre du personnel n’est en ligne actuellement.',
    'tc.voiceCall': 'Appel vocal',
    'tc.videoCall': 'Appel vidéo',
    'tc.selectedLabel': 'Sélectionné : {name}',
    'tc.stat.totalCalls': 'Total des appels',
    'tc.stat.successful': 'Réussis',
    'tc.stat.successRate': 'Taux de réussite',
    'tc.stat.onlineNow': 'En ligne maintenant',
    'tc.stat.completedCalls': 'Appels terminés',
    'tc.teamCallHistory': 'Historique des appels de l’équipe',
    'tc.myCallHistory': 'Mon historique d’appels',
    'tc.history.supervisorDesc': 'Vous consultez l’historique des appels de toute votre équipe (accès de supervision).',
    'tc.history.ownDesc': 'Vous consultez uniquement votre propre historique d’appels.',
    'tc.history.loading': 'Chargement de l’historique des appels...',
    'tc.history.empty': 'Aucun historique d’appels disponible',
    'tc.history.emptyHint': 'Les appels apparaîtront ici une fois que vous commencerez à en passer',
    'tc.quickActions': 'Actions rapides',
    'tc.startVoiceCall': 'Démarrer un appel vocal',
    'tc.startVideoCall': 'Démarrer un appel vidéo',
    'tc.startCustomerConversation': 'Démarrer une conversation client',
    'tc.startAudioConference': 'Démarrer une conférence audio',
    'tc.conf.inProgress': 'Conférence en cours',
    'tc.conf.startTitle': 'Démarrer une conférence audio avec les membres de l’équipe en ligne',
    'tc.conf.inProgressTitle': 'Une conférence est déjà en cours',
    'tc.conf.manageHint': 'Gérez les participants dans le panneau de la conférence.',
    'tc.conf.audioOnlyHint': 'Audio uniquement. Les coéquipiers en ligne sont invités ; ajoutez-en d’autres depuis le panneau.',
    'tc.refreshStatistics': 'Actualiser les statistiques',
    'tc.refreshing': 'Actualisation…',
    'tc.lastRefreshed': 'Dernière actualisation',
    'tc.callPerformance': 'Performance des appels',
    'tc.analytics.empty': 'Aucune donnée analytique disponible',
    'tc.connectionStatus': 'État de la connexion',
    'tc.webrtcStatus': 'État WebRTC',
    'tc.onlineUsers': 'Utilisateurs en ligne',
    'tc.currentCall': 'Appel en cours',
    'tc.teamConv.title': 'Démarrer une conversation d’équipe',
    'tc.teamConv.desc': 'Sélectionnez des membres de l’équipe et rédigez un message pour démarrer une conversation de groupe',
    'tc.teamConv.selectMembers': 'Sélectionner les membres de l’équipe',
    'tc.teamConv.membersSelected': '{count} membre(s) sélectionné(s)',
    'tc.teamConv.message': 'Message',
    'tc.teamConv.messagePlaceholder': 'Saisissez votre message pour l’équipe...',
    'tc.startConversation': 'Démarrer la conversation',
    'tc.noStaffYet': 'Aucun membre du personnel dans cette entreprise pour le moment.',
    'tc.you': '(Vous)',
    'tc.createTicket.title': 'Créer un ticket d’assistance client',
    'tc.createTicket.desc': 'Sélectionnez un client et l’une de ses conversations, puis décrivez le problème.',
    'tc.createTicket.customerReq': 'Client *',
    'tc.createTicket.conversationReq': 'Conversation *',
    'tc.createTicket.titleReq': 'Titre du ticket *',
    'tc.createTicket.descriptionReq': 'Description *',
    'tc.createTicket.loadingCustomers': 'Chargement des clients…',
    'tc.createTicket.selectCustomer': 'Sélectionnez un client',
    'tc.createTicket.selectCustomerFirst': 'Sélectionnez d’abord un client',
    'tc.createTicket.loadingConversations': 'Chargement des conversations…',
    'tc.createTicket.noConversations': 'Aucune conversation pour ce client',
    'tc.createTicket.selectConversation': 'Sélectionnez une conversation',
    'tc.createTicket.titlePlaceholder': 'Brève description du problème',
    'tc.createTicket.descriptionPlaceholder': 'Description détaillée du problème ou de la demande',
    'tc.createTicket.priority': 'Priorité',
    'tc.createTicket.category': 'Catégorie',
    'tc.createTicket.create': 'Créer le ticket',
    'tc.createTicket.creating': 'Création…',
    'tc.priority.low': 'Basse',
    'tc.priority.medium': 'Moyenne',
    'tc.priority.high': 'Haute',
    'tc.priority.urgent': 'Urgente',
    'tc.category.general': 'Assistance générale',
    'tc.category.technical': 'Problème technique',
    'tc.category.billing': 'Facturation',
    'tc.category.feature': 'Demande de fonctionnalité',
    'tc.newConv.title': 'Démarrer une nouvelle conversation',
    'tc.newConv.desc': 'Saisissez les coordonnées du client pour démarrer une nouvelle conversation de chat',
    'tc.newConv.nameReq': 'Nom du client *',
    'tc.newConv.namePlaceholder': 'Saisissez le nom du client',
    'tc.newConv.phoneOptional': 'Numéro de téléphone (facultatif)',
    'tc.newConv.startChat': 'Démarrer le chat',
    'tc.toast.noStaffOnline.title': 'Aucun personnel en ligne',
    'tc.toast.noStaffOnline.desc': 'Aucun autre membre du personnel n’est en ligne actuellement.',
    'tc.teamMembers': 'Membres de l’équipe',
    'tc.refreshStaffPresence': 'Actualiser le personnel et la présence',
    'tc.loadingStaff': 'Chargement du personnel…',
    'tc.loadStaffError': 'Impossible de charger le personnel. Appuyez sur actualiser pour réessayer.',
    'tc.thisIsYou': 'C’est vous',
    'tc.chat': 'Chat',
    'tc.chatUnread': 'Chat · {count} non lu(s)',
    'tc.presenceReconnecting': 'Reconnexion de la présence en direct… le statut en ligne/hors ligne peut être momentanément obsolète.',
    'tc.videoCallInProgress': 'Appel vidéo en cours...',
    'tc.voiceCallInProgress': 'Appel vocal en cours...',
    'tc.chatModal.privateDesc': 'Chat privé du personnel — seuls vous et {name} pouvez voir ces messages.',
    'tc.chatModal.opening': 'Ouverture du chat…',
    'tc.chatModal.noMessages': 'Aucun message pour le moment. Dites bonjour.',
    'tc.chatModal.inputPlaceholder': 'Saisissez un message…',
    'tc.chatModal.sending': 'Envoi…',
    'tc.chatModal.failed': 'Échec ·',
  },
  pt: {
    'tc.quickCall.desc': 'Escolha como deseja fazer sua chamada',
    'tc.tab.dialpad': 'Teclado',
    'tc.tab.recent': 'Recentes',
    'tc.tab.contacts': 'Contatos',
    'tc.tab.activeComm': 'Comunicação ativa',
    'tc.tab.analytics': 'Análises',
    'tc.dialpad.phonePlaceholder': 'Insira o número de telefone do funcionário',
    'tc.dialpad.help': 'Apenas entre funcionários — liga para um funcionário do locatário por telefone. A discagem externa (PSTN) não é suportada.',
    'tc.clear': 'Limpar',
    'tc.call': 'Ligar',
    'tc.noRecentCalls': 'Ainda não há chamadas recentes de funcionários.',
    'tc.noOtherStaffOnline': 'Nenhum outro funcionário está online no momento.',
    'tc.voiceCall': 'Chamada de voz',
    'tc.videoCall': 'Chamada de vídeo',
    'tc.selectedLabel': 'Selecionado: {name}',
    'tc.stat.totalCalls': 'Total de chamadas',
    'tc.stat.successful': 'Bem-sucedidas',
    'tc.stat.successRate': 'Taxa de sucesso',
    'tc.stat.onlineNow': 'Online agora',
    'tc.stat.completedCalls': 'Chamadas concluídas',
    'tc.teamCallHistory': 'Histórico de chamadas da equipe',
    'tc.myCallHistory': 'Meu histórico de chamadas',
    'tc.history.supervisorDesc': 'Você está visualizando o histórico de chamadas de toda a sua equipe (acesso de supervisão).',
    'tc.history.ownDesc': 'Você está visualizando apenas seu próprio histórico de chamadas.',
    'tc.history.loading': 'Carregando histórico de chamadas...',
    'tc.history.empty': 'Nenhum histórico de chamadas disponível',
    'tc.history.emptyHint': 'As chamadas aparecerão aqui assim que você começar a fazê-las',
    'tc.quickActions': 'Ações rápidas',
    'tc.startVoiceCall': 'Iniciar chamada de voz',
    'tc.startVideoCall': 'Iniciar chamada de vídeo',
    'tc.startCustomerConversation': 'Iniciar conversa com o cliente',
    'tc.startAudioConference': 'Iniciar conferência de áudio',
    'tc.conf.inProgress': 'Conferência em andamento',
    'tc.conf.startTitle': 'Iniciar uma conferência de áudio com os membros da equipe online',
    'tc.conf.inProgressTitle': 'Já há uma conferência em andamento',
    'tc.conf.manageHint': 'Gerencie os participantes no painel da conferência.',
    'tc.conf.audioOnlyHint': 'Apenas áudio. Os colegas online são convidados; adicione mais pelo painel.',
    'tc.refreshStatistics': 'Atualizar estatísticas',
    'tc.refreshing': 'Atualizando…',
    'tc.lastRefreshed': 'Última atualização',
    'tc.callPerformance': 'Desempenho das chamadas',
    'tc.analytics.empty': 'Nenhum dado analítico disponível',
    'tc.connectionStatus': 'Status da conexão',
    'tc.webrtcStatus': 'Status do WebRTC',
    'tc.onlineUsers': 'Usuários online',
    'tc.currentCall': 'Chamada atual',
    'tc.teamConv.title': 'Iniciar conversa da equipe',
    'tc.teamConv.desc': 'Selecione os membros da equipe e componha uma mensagem para iniciar uma conversa em grupo',
    'tc.teamConv.selectMembers': 'Selecionar membros da equipe',
    'tc.teamConv.membersSelected': '{count} membro(s) selecionado(s)',
    'tc.teamConv.message': 'Mensagem',
    'tc.teamConv.messagePlaceholder': 'Digite sua mensagem para a equipe...',
    'tc.startConversation': 'Iniciar conversa',
    'tc.noStaffYet': 'Ainda não há funcionários nesta empresa.',
    'tc.you': '(Você)',
    'tc.createTicket.title': 'Criar ticket de suporte ao cliente',
    'tc.createTicket.desc': 'Selecione um cliente e uma de suas conversas e descreva o problema.',
    'tc.createTicket.customerReq': 'Cliente *',
    'tc.createTicket.conversationReq': 'Conversa *',
    'tc.createTicket.titleReq': 'Título do ticket *',
    'tc.createTicket.descriptionReq': 'Descrição *',
    'tc.createTicket.loadingCustomers': 'Carregando clientes…',
    'tc.createTicket.selectCustomer': 'Selecione um cliente',
    'tc.createTicket.selectCustomerFirst': 'Selecione um cliente primeiro',
    'tc.createTicket.loadingConversations': 'Carregando conversas…',
    'tc.createTicket.noConversations': 'Nenhuma conversa para este cliente',
    'tc.createTicket.selectConversation': 'Selecione uma conversa',
    'tc.createTicket.titlePlaceholder': 'Breve descrição do problema',
    'tc.createTicket.descriptionPlaceholder': 'Descrição detalhada do problema ou solicitação',
    'tc.createTicket.priority': 'Prioridade',
    'tc.createTicket.category': 'Categoria',
    'tc.createTicket.create': 'Criar ticket',
    'tc.createTicket.creating': 'Criando…',
    'tc.priority.low': 'Baixa',
    'tc.priority.medium': 'Média',
    'tc.priority.high': 'Alta',
    'tc.priority.urgent': 'Urgente',
    'tc.category.general': 'Suporte geral',
    'tc.category.technical': 'Problema técnico',
    'tc.category.billing': 'Faturamento',
    'tc.category.feature': 'Solicitação de recurso',
    'tc.newConv.title': 'Iniciar nova conversa',
    'tc.newConv.desc': 'Insira os dados do cliente para iniciar uma nova conversa de chat',
    'tc.newConv.nameReq': 'Nome do cliente *',
    'tc.newConv.namePlaceholder': 'Insira o nome do cliente',
    'tc.newConv.phoneOptional': 'Número de telefone (opcional)',
    'tc.newConv.startChat': 'Iniciar chat',
    'tc.toast.noStaffOnline.title': 'Nenhum funcionário online',
    'tc.toast.noStaffOnline.desc': 'Nenhum outro funcionário está online no momento.',
    'tc.teamMembers': 'Membros da equipe',
    'tc.refreshStaffPresence': 'Atualizar funcionários e presença',
    'tc.loadingStaff': 'Carregando funcionários…',
    'tc.loadStaffError': 'Não foi possível carregar os funcionários. Toque em atualizar para tentar novamente.',
    'tc.thisIsYou': 'Este é você',
    'tc.chat': 'Chat',
    'tc.chatUnread': 'Chat · {count} não lida(s)',
    'tc.presenceReconnecting': 'Reconectando a presença ao vivo… o status online/offline pode estar momentaneamente desatualizado.',
    'tc.videoCallInProgress': 'Chamada de vídeo em andamento...',
    'tc.voiceCallInProgress': 'Chamada de voz em andamento...',
    'tc.chatModal.privateDesc': 'Chat privado de funcionários — apenas você e {name} podem ver estas mensagens.',
    'tc.chatModal.opening': 'Abrindo chat…',
    'tc.chatModal.noMessages': 'Ainda não há mensagens. Diga olá.',
    'tc.chatModal.inputPlaceholder': 'Digite uma mensagem…',
    'tc.chatModal.sending': 'Enviando…',
    'tc.chatModal.failed': 'Falhou ·',
  },
  de: {
    'tc.quickCall.desc': 'Wählen Sie, wie Sie Ihren Anruf tätigen möchten',
    'tc.tab.dialpad': 'Wähltastatur',
    'tc.tab.recent': 'Kürzlich',
    'tc.tab.contacts': 'Kontakte',
    'tc.tab.activeComm': 'Aktive Kommunikation',
    'tc.tab.analytics': 'Analysen',
    'tc.dialpad.phonePlaceholder': 'Telefonnummer des Mitarbeiters eingeben',
    'tc.dialpad.help': 'Nur zwischen Mitarbeitern — ruft einen Mitarbeiter des Mandanten per Telefon an. Externe (PSTN-)Anwahl wird nicht unterstützt.',
    'tc.clear': 'Löschen',
    'tc.call': 'Anrufen',
    'tc.noRecentCalls': 'Noch keine aktuellen Mitarbeiteranrufe.',
    'tc.noOtherStaffOnline': 'Derzeit sind keine anderen Mitarbeiter online.',
    'tc.voiceCall': 'Sprachanruf',
    'tc.videoCall': 'Videoanruf',
    'tc.selectedLabel': 'Ausgewählt: {name}',
    'tc.stat.totalCalls': 'Anrufe gesamt',
    'tc.stat.successful': 'Erfolgreich',
    'tc.stat.successRate': 'Erfolgsquote',
    'tc.stat.onlineNow': 'Jetzt online',
    'tc.stat.completedCalls': 'Abgeschlossene Anrufe',
    'tc.teamCallHistory': 'Anrufverlauf des Teams',
    'tc.myCallHistory': 'Mein Anrufverlauf',
    'tc.history.supervisorDesc': 'Sie sehen den Anrufverlauf Ihres gesamten Teams (Aufsichtszugriff).',
    'tc.history.ownDesc': 'Sie sehen nur Ihren eigenen Anrufverlauf.',
    'tc.history.loading': 'Anrufverlauf wird geladen...',
    'tc.history.empty': 'Kein Anrufverlauf verfügbar',
    'tc.history.emptyHint': 'Anrufe werden hier angezeigt, sobald Sie welche tätigen',
    'tc.quickActions': 'Schnellaktionen',
    'tc.startVoiceCall': 'Sprachanruf starten',
    'tc.startVideoCall': 'Videoanruf starten',
    'tc.startCustomerConversation': 'Kundengespräch starten',
    'tc.startAudioConference': 'Audiokonferenz starten',
    'tc.conf.inProgress': 'Konferenz läuft',
    'tc.conf.startTitle': 'Eine Audiokonferenz mit den Online-Teammitgliedern starten',
    'tc.conf.inProgressTitle': 'Eine Konferenz läuft bereits',
    'tc.conf.manageHint': 'Verwalten Sie die Teilnehmer im Konferenzbereich.',
    'tc.conf.audioOnlyHint': 'Nur Audio. Online-Teamkollegen werden eingeladen; fügen Sie weitere über den Bereich hinzu.',
    'tc.refreshStatistics': 'Statistiken aktualisieren',
    'tc.refreshing': 'Wird aktualisiert…',
    'tc.lastRefreshed': 'Zuletzt aktualisiert',
    'tc.callPerformance': 'Anrufleistung',
    'tc.analytics.empty': 'Keine Analysedaten verfügbar',
    'tc.connectionStatus': 'Verbindungsstatus',
    'tc.webrtcStatus': 'WebRTC-Status',
    'tc.onlineUsers': 'Online-Benutzer',
    'tc.currentCall': 'Aktueller Anruf',
    'tc.teamConv.title': 'Teamgespräch starten',
    'tc.teamConv.desc': 'Wählen Sie Teammitglieder aus und verfassen Sie eine Nachricht, um ein Gruppengespräch zu starten',
    'tc.teamConv.selectMembers': 'Teammitglieder auswählen',
    'tc.teamConv.membersSelected': '{count} Mitglied(er) ausgewählt',
    'tc.teamConv.message': 'Nachricht',
    'tc.teamConv.messagePlaceholder': 'Geben Sie Ihre Nachricht an das Team ein...',
    'tc.startConversation': 'Gespräch starten',
    'tc.noStaffYet': 'Noch keine Mitarbeiter in diesem Unternehmen.',
    'tc.you': '(Sie)',
    'tc.createTicket.title': 'Kundensupport-Ticket erstellen',
    'tc.createTicket.desc': 'Wählen Sie einen Kunden und eine seiner Konversationen aus und beschreiben Sie dann das Problem.',
    'tc.createTicket.customerReq': 'Kunde *',
    'tc.createTicket.conversationReq': 'Konversation *',
    'tc.createTicket.titleReq': 'Ticket-Titel *',
    'tc.createTicket.descriptionReq': 'Beschreibung *',
    'tc.createTicket.loadingCustomers': 'Kunden werden geladen…',
    'tc.createTicket.selectCustomer': 'Wählen Sie einen Kunden',
    'tc.createTicket.selectCustomerFirst': 'Wählen Sie zuerst einen Kunden',
    'tc.createTicket.loadingConversations': 'Konversationen werden geladen…',
    'tc.createTicket.noConversations': 'Keine Konversationen für diesen Kunden',
    'tc.createTicket.selectConversation': 'Wählen Sie eine Konversation',
    'tc.createTicket.titlePlaceholder': 'Kurze Beschreibung des Problems',
    'tc.createTicket.descriptionPlaceholder': 'Detaillierte Beschreibung des Problems oder der Anfrage',
    'tc.createTicket.priority': 'Priorität',
    'tc.createTicket.category': 'Kategorie',
    'tc.createTicket.create': 'Ticket erstellen',
    'tc.createTicket.creating': 'Wird erstellt…',
    'tc.priority.low': 'Niedrig',
    'tc.priority.medium': 'Mittel',
    'tc.priority.high': 'Hoch',
    'tc.priority.urgent': 'Dringend',
    'tc.category.general': 'Allgemeiner Support',
    'tc.category.technical': 'Technisches Problem',
    'tc.category.billing': 'Abrechnung',
    'tc.category.feature': 'Funktionswunsch',
    'tc.newConv.title': 'Neues Gespräch starten',
    'tc.newConv.desc': 'Geben Sie die Kundendaten ein, um ein neues Chat-Gespräch zu starten',
    'tc.newConv.nameReq': 'Kundenname *',
    'tc.newConv.namePlaceholder': 'Kundennamen eingeben',
    'tc.newConv.phoneOptional': 'Telefonnummer (optional)',
    'tc.newConv.startChat': 'Chat starten',
    'tc.toast.noStaffOnline.title': 'Keine Mitarbeiter online',
    'tc.toast.noStaffOnline.desc': 'Derzeit sind keine anderen Mitarbeiter online.',
    'tc.teamMembers': 'Teammitglieder',
    'tc.refreshStaffPresence': 'Mitarbeiter & Präsenz aktualisieren',
    'tc.loadingStaff': 'Mitarbeiter werden geladen…',
    'tc.loadStaffError': 'Mitarbeiter konnten nicht geladen werden. Zum Wiederholen auf Aktualisieren tippen.',
    'tc.thisIsYou': 'Das sind Sie',
    'tc.chat': 'Chat',
    'tc.chatUnread': 'Chat · {count} ungelesen',
    'tc.presenceReconnecting': 'Live-Präsenz wird neu verbunden… Online-/Offline-Status kann vorübergehend veraltet sein.',
    'tc.videoCallInProgress': 'Videoanruf läuft...',
    'tc.voiceCallInProgress': 'Sprachanruf läuft...',
    'tc.chatModal.privateDesc': 'Privater Mitarbeiter-Chat — nur Sie und {name} können diese Nachrichten sehen.',
    'tc.chatModal.opening': 'Chat wird geöffnet…',
    'tc.chatModal.noMessages': 'Noch keine Nachrichten. Sagen Sie Hallo.',
    'tc.chatModal.inputPlaceholder': 'Nachricht eingeben…',
    'tc.chatModal.sending': 'Wird gesendet…',
    'tc.chatModal.failed': 'Fehlgeschlagen ·',
  },
  it: {
    'tc.quickCall.desc': 'Scegli come effettuare la chiamata',
    'tc.tab.dialpad': 'Tastierino',
    'tc.tab.recent': 'Recenti',
    'tc.tab.contacts': 'Contatti',
    'tc.tab.activeComm': 'Comunicazione attiva',
    'tc.tab.analytics': 'Analisi',
    'tc.dialpad.phonePlaceholder': 'Inserisci il numero di telefono del personale',
    'tc.dialpad.help': 'Solo tra membri del personale — chiama un membro del personale del tenant tramite telefono. La composizione esterna (PSTN) non è supportata.',
    'tc.clear': 'Cancella',
    'tc.call': 'Chiama',
    'tc.noRecentCalls': 'Ancora nessuna chiamata recente del personale.',
    'tc.noOtherStaffOnline': 'Nessun altro membro del personale è attualmente online.',
    'tc.voiceCall': 'Chiamata vocale',
    'tc.videoCall': 'Videochiamata',
    'tc.selectedLabel': 'Selezionato: {name}',
    'tc.stat.totalCalls': 'Chiamate totali',
    'tc.stat.successful': 'Riuscite',
    'tc.stat.successRate': 'Tasso di successo',
    'tc.stat.onlineNow': 'Online ora',
    'tc.stat.completedCalls': 'Chiamate completate',
    'tc.teamCallHistory': 'Cronologia chiamate del team',
    'tc.myCallHistory': 'La mia cronologia chiamate',
    'tc.history.supervisorDesc': 'Stai visualizzando la cronologia chiamate dell’intero team (accesso di supervisione).',
    'tc.history.ownDesc': 'Stai visualizzando solo la tua cronologia chiamate.',
    'tc.history.loading': 'Caricamento della cronologia chiamate...',
    'tc.history.empty': 'Nessuna cronologia chiamate disponibile',
    'tc.history.emptyHint': 'Le chiamate appariranno qui una volta che inizierai a effettuarle',
    'tc.quickActions': 'Azioni rapide',
    'tc.startVoiceCall': 'Avvia chiamata vocale',
    'tc.startVideoCall': 'Avvia videochiamata',
    'tc.startCustomerConversation': 'Avvia conversazione con il cliente',
    'tc.startAudioConference': 'Avvia conferenza audio',
    'tc.conf.inProgress': 'Conferenza in corso',
    'tc.conf.startTitle': 'Avvia una conferenza audio con i membri del team online',
    'tc.conf.inProgressTitle': 'È già in corso una conferenza',
    'tc.conf.manageHint': 'Gestisci i partecipanti nel pannello della conferenza.',
    'tc.conf.audioOnlyHint': 'Solo audio. I colleghi online sono invitati; aggiungine altri dal pannello.',
    'tc.refreshStatistics': 'Aggiorna statistiche',
    'tc.refreshing': 'Aggiornamento…',
    'tc.lastRefreshed': 'Ultimo aggiornamento',
    'tc.callPerformance': 'Prestazioni delle chiamate',
    'tc.analytics.empty': 'Nessun dato analitico disponibile',
    'tc.connectionStatus': 'Stato della connessione',
    'tc.webrtcStatus': 'Stato WebRTC',
    'tc.onlineUsers': 'Utenti online',
    'tc.currentCall': 'Chiamata corrente',
    'tc.teamConv.title': 'Avvia conversazione di team',
    'tc.teamConv.desc': 'Seleziona i membri del team e componi un messaggio per avviare una conversazione di gruppo',
    'tc.teamConv.selectMembers': 'Seleziona i membri del team',
    'tc.teamConv.membersSelected': '{count} membro/i selezionato/i',
    'tc.teamConv.message': 'Messaggio',
    'tc.teamConv.messagePlaceholder': 'Scrivi il tuo messaggio al team...',
    'tc.startConversation': 'Avvia conversazione',
    'tc.noStaffYet': 'Ancora nessun membro del personale in questa azienda.',
    'tc.you': '(Tu)',
    'tc.createTicket.title': 'Crea ticket di assistenza clienti',
    'tc.createTicket.desc': 'Seleziona un cliente e una delle sue conversazioni, quindi descrivi il problema.',
    'tc.createTicket.customerReq': 'Cliente *',
    'tc.createTicket.conversationReq': 'Conversazione *',
    'tc.createTicket.titleReq': 'Titolo del ticket *',
    'tc.createTicket.descriptionReq': 'Descrizione *',
    'tc.createTicket.loadingCustomers': 'Caricamento clienti…',
    'tc.createTicket.selectCustomer': 'Seleziona un cliente',
    'tc.createTicket.selectCustomerFirst': 'Seleziona prima un cliente',
    'tc.createTicket.loadingConversations': 'Caricamento conversazioni…',
    'tc.createTicket.noConversations': 'Nessuna conversazione per questo cliente',
    'tc.createTicket.selectConversation': 'Seleziona una conversazione',
    'tc.createTicket.titlePlaceholder': 'Breve descrizione del problema',
    'tc.createTicket.descriptionPlaceholder': 'Descrizione dettagliata del problema o della richiesta',
    'tc.createTicket.priority': 'Priorità',
    'tc.createTicket.category': 'Categoria',
    'tc.createTicket.create': 'Crea ticket',
    'tc.createTicket.creating': 'Creazione…',
    'tc.priority.low': 'Bassa',
    'tc.priority.medium': 'Media',
    'tc.priority.high': 'Alta',
    'tc.priority.urgent': 'Urgente',
    'tc.category.general': 'Assistenza generale',
    'tc.category.technical': 'Problema tecnico',
    'tc.category.billing': 'Fatturazione',
    'tc.category.feature': 'Richiesta di funzionalità',
    'tc.newConv.title': 'Avvia nuova conversazione',
    'tc.newConv.desc': 'Inserisci i dati del cliente per avviare una nuova conversazione in chat',
    'tc.newConv.nameReq': 'Nome del cliente *',
    'tc.newConv.namePlaceholder': 'Inserisci il nome del cliente',
    'tc.newConv.phoneOptional': 'Numero di telefono (facoltativo)',
    'tc.newConv.startChat': 'Avvia chat',
    'tc.toast.noStaffOnline.title': 'Nessun membro del personale online',
    'tc.toast.noStaffOnline.desc': 'Nessun altro membro del personale è attualmente online.',
    'tc.teamMembers': 'Membri del team',
    'tc.refreshStaffPresence': 'Aggiorna personale e presenza',
    'tc.loadingStaff': 'Caricamento del personale…',
    'tc.loadStaffError': 'Impossibile caricare il personale. Tocca aggiorna per riprovare.',
    'tc.thisIsYou': 'Questo sei tu',
    'tc.chat': 'Chat',
    'tc.chatUnread': 'Chat · {count} non letto/i',
    'tc.presenceReconnecting': 'Riconnessione della presenza in tempo reale… lo stato online/offline potrebbe essere momentaneamente non aggiornato.',
    'tc.videoCallInProgress': 'Videochiamata in corso...',
    'tc.voiceCallInProgress': 'Chiamata vocale in corso...',
    'tc.chatModal.privateDesc': 'Chat privata del personale — solo tu e {name} potete vedere questi messaggi.',
    'tc.chatModal.opening': 'Apertura della chat…',
    'tc.chatModal.noMessages': 'Ancora nessun messaggio. Saluta.',
    'tc.chatModal.inputPlaceholder': 'Scrivi un messaggio…',
    'tc.chatModal.sending': 'Invio…',
    'tc.chatModal.failed': 'Non riuscito ·',
  },
  ja: {
    'tc.quickCall.desc': '通話方法を選択してください',
    'tc.tab.dialpad': 'ダイヤルパッド',
    'tc.tab.recent': '最近',
    'tc.tab.contacts': '連絡先',
    'tc.tab.activeComm': 'アクティブな通信',
    'tc.tab.analytics': '分析',
    'tc.dialpad.phonePlaceholder': 'スタッフの電話番号を入力',
    'tc.dialpad.help': 'スタッフ間のみ — テナントのスタッフに電話番号で発信します。外部（PSTN）発信はサポートされていません。',
    'tc.clear': 'クリア',
    'tc.call': '発信',
    'tc.noRecentCalls': '最近のスタッフ通話はまだありません。',
    'tc.noOtherStaffOnline': '現在オンラインの他のスタッフはいません。',
    'tc.voiceCall': '音声通話',
    'tc.videoCall': 'ビデオ通話',
    'tc.selectedLabel': '選択済み: {name}',
    'tc.stat.totalCalls': '総通話数',
    'tc.stat.successful': '成功',
    'tc.stat.successRate': '成功率',
    'tc.stat.onlineNow': '現在オンライン',
    'tc.stat.completedCalls': '完了した通話',
    'tc.teamCallHistory': 'チームの通話履歴',
    'tc.myCallHistory': '自分の通話履歴',
    'tc.history.supervisorDesc': 'チーム全体の通話履歴を表示しています（監督者アクセス）。',
    'tc.history.ownDesc': '自分自身の通話履歴のみを表示しています。',
    'tc.history.loading': '通話履歴を読み込み中...',
    'tc.history.empty': '利用可能な通話履歴はありません',
    'tc.history.emptyHint': '通話を開始すると、ここに表示されます',
    'tc.quickActions': 'クイックアクション',
    'tc.startVoiceCall': '音声通話を開始',
    'tc.startVideoCall': 'ビデオ通話を開始',
    'tc.startCustomerConversation': '顧客との会話を開始',
    'tc.startAudioConference': '音声会議を開始',
    'tc.conf.inProgress': '会議進行中',
    'tc.conf.startTitle': 'オンラインのチームメンバーと音声会議を開始',
    'tc.conf.inProgressTitle': 'すでに会議が進行中です',
    'tc.conf.manageHint': '会議パネルで参加者を管理します。',
    'tc.conf.audioOnlyHint': '音声のみ。オンラインのチームメンバーが招待されます。パネルから追加できます。',
    'tc.refreshStatistics': '統計を更新',
    'tc.refreshing': '更新中…',
    'tc.lastRefreshed': '最終更新',
    'tc.callPerformance': '通話パフォーマンス',
    'tc.analytics.empty': '利用可能な分析データはありません',
    'tc.connectionStatus': '接続状態',
    'tc.webrtcStatus': 'WebRTC の状態',
    'tc.onlineUsers': 'オンラインユーザー',
    'tc.currentCall': '現在の通話',
    'tc.teamConv.title': 'チーム会話を開始',
    'tc.teamConv.desc': 'チームメンバーを選択し、メッセージを作成してグループ会話を開始します',
    'tc.teamConv.selectMembers': 'チームメンバーを選択',
    'tc.teamConv.membersSelected': '{count} 名を選択',
    'tc.teamConv.message': 'メッセージ',
    'tc.teamConv.messagePlaceholder': 'チームへのメッセージを入力...',
    'tc.startConversation': '会話を開始',
    'tc.noStaffYet': 'このビジネスにはまだスタッフがいません。',
    'tc.you': '（あなた）',
    'tc.createTicket.title': '顧客サポートチケットを作成',
    'tc.createTicket.desc': '顧客とその会話の1つを選択し、問題を説明してください。',
    'tc.createTicket.customerReq': '顧客 *',
    'tc.createTicket.conversationReq': '会話 *',
    'tc.createTicket.titleReq': 'チケットのタイトル *',
    'tc.createTicket.descriptionReq': '説明 *',
    'tc.createTicket.loadingCustomers': '顧客を読み込み中…',
    'tc.createTicket.selectCustomer': '顧客を選択',
    'tc.createTicket.selectCustomerFirst': '先に顧客を選択してください',
    'tc.createTicket.loadingConversations': '会話を読み込み中…',
    'tc.createTicket.noConversations': 'この顧客の会話はありません',
    'tc.createTicket.selectConversation': '会話を選択',
    'tc.createTicket.titlePlaceholder': '問題の簡単な説明',
    'tc.createTicket.descriptionPlaceholder': '問題または要望の詳細な説明',
    'tc.createTicket.priority': '優先度',
    'tc.createTicket.category': 'カテゴリー',
    'tc.createTicket.create': 'チケットを作成',
    'tc.createTicket.creating': '作成中…',
    'tc.priority.low': '低',
    'tc.priority.medium': '中',
    'tc.priority.high': '高',
    'tc.priority.urgent': '緊急',
    'tc.category.general': '一般サポート',
    'tc.category.technical': '技術的な問題',
    'tc.category.billing': '請求',
    'tc.category.feature': '機能リクエスト',
    'tc.newConv.title': '新しい会話を開始',
    'tc.newConv.desc': '顧客情報を入力して新しいチャット会話を開始します',
    'tc.newConv.nameReq': '顧客名 *',
    'tc.newConv.namePlaceholder': '顧客名を入力',
    'tc.newConv.phoneOptional': '電話番号（任意）',
    'tc.newConv.startChat': 'チャットを開始',
    'tc.toast.noStaffOnline.title': 'オンラインのスタッフがいません',
    'tc.toast.noStaffOnline.desc': '現在オンラインの他のスタッフはいません。',
    'tc.teamMembers': 'チームメンバー',
    'tc.refreshStaffPresence': 'スタッフと在席状況を更新',
    'tc.loadingStaff': 'スタッフを読み込み中…',
    'tc.loadStaffError': 'スタッフを読み込めませんでした。更新をタップして再試行してください。',
    'tc.thisIsYou': 'これはあなたです',
    'tc.chat': 'チャット',
    'tc.chatUnread': 'チャット · 未読 {count} 件',
    'tc.presenceReconnecting': 'ライブ在席状況を再接続中… オンライン/オフラインの状態が一時的に古い場合があります。',
    'tc.videoCallInProgress': 'ビデオ通話中...',
    'tc.voiceCallInProgress': '音声通話中...',
    'tc.chatModal.privateDesc': 'スタッフのプライベートチャット — あなたと {name} だけがこれらのメッセージを表示できます。',
    'tc.chatModal.opening': 'チャットを開いています…',
    'tc.chatModal.noMessages': 'まだメッセージはありません。あいさつしましょう。',
    'tc.chatModal.inputPlaceholder': 'メッセージを入力…',
    'tc.chatModal.sending': '送信中…',
    'tc.chatModal.failed': '失敗 ·',
  },
  ko: {
    'tc.quickCall.desc': '통화 방법을 선택하세요',
    'tc.tab.dialpad': '다이얼패드',
    'tc.tab.recent': '최근',
    'tc.tab.contacts': '연락처',
    'tc.tab.activeComm': '활성 통신',
    'tc.tab.analytics': '분석',
    'tc.dialpad.phonePlaceholder': '직원 전화번호 입력',
    'tc.dialpad.help': '직원 간 전용 — 전화번호로 테넌트 직원에게 전화를 겁니다. 외부(PSTN) 발신은 지원되지 않습니다.',
    'tc.clear': '지우기',
    'tc.call': '전화',
    'tc.noRecentCalls': '아직 최근 직원 통화가 없습니다.',
    'tc.noOtherStaffOnline': '현재 온라인 상태인 다른 직원이 없습니다.',
    'tc.voiceCall': '음성 통화',
    'tc.videoCall': '영상 통화',
    'tc.selectedLabel': '선택됨: {name}',
    'tc.stat.totalCalls': '총 통화',
    'tc.stat.successful': '성공',
    'tc.stat.successRate': '성공률',
    'tc.stat.onlineNow': '현재 온라인',
    'tc.stat.completedCalls': '완료된 통화',
    'tc.teamCallHistory': '팀 통화 기록',
    'tc.myCallHistory': '내 통화 기록',
    'tc.history.supervisorDesc': '팀 전체의 통화 기록을 보고 있습니다(감독 권한).',
    'tc.history.ownDesc': '본인의 통화 기록만 보고 있습니다.',
    'tc.history.loading': '통화 기록을 불러오는 중...',
    'tc.history.empty': '사용 가능한 통화 기록이 없습니다',
    'tc.history.emptyHint': '통화를 시작하면 여기에 표시됩니다',
    'tc.quickActions': '빠른 작업',
    'tc.startVoiceCall': '음성 통화 시작',
    'tc.startVideoCall': '영상 통화 시작',
    'tc.startCustomerConversation': '고객 대화 시작',
    'tc.startAudioConference': '오디오 회의 시작',
    'tc.conf.inProgress': '회의 진행 중',
    'tc.conf.startTitle': '온라인 팀원과 오디오 회의 시작',
    'tc.conf.inProgressTitle': '이미 회의가 진행 중입니다',
    'tc.conf.manageHint': '회의 패널에서 참가자를 관리하세요.',
    'tc.conf.audioOnlyHint': '오디오 전용. 온라인 팀원이 초대됩니다. 패널에서 더 추가하세요.',
    'tc.refreshStatistics': '통계 새로 고침',
    'tc.refreshing': '새로 고침 중…',
    'tc.lastRefreshed': '마지막 새로 고침',
    'tc.callPerformance': '통화 성과',
    'tc.analytics.empty': '사용 가능한 분석 데이터가 없습니다',
    'tc.connectionStatus': '연결 상태',
    'tc.webrtcStatus': 'WebRTC 상태',
    'tc.onlineUsers': '온라인 사용자',
    'tc.currentCall': '현재 통화',
    'tc.teamConv.title': '팀 대화 시작',
    'tc.teamConv.desc': '팀원을 선택하고 메시지를 작성하여 그룹 대화를 시작하세요',
    'tc.teamConv.selectMembers': '팀원 선택',
    'tc.teamConv.membersSelected': '{count}명 선택됨',
    'tc.teamConv.message': '메시지',
    'tc.teamConv.messagePlaceholder': '팀에게 보낼 메시지를 입력하세요...',
    'tc.startConversation': '대화 시작',
    'tc.noStaffYet': '아직 이 비즈니스에 직원이 없습니다.',
    'tc.you': '(나)',
    'tc.createTicket.title': '고객 지원 티켓 만들기',
    'tc.createTicket.desc': '고객과 해당 고객의 대화 중 하나를 선택한 다음 문제를 설명하세요.',
    'tc.createTicket.customerReq': '고객 *',
    'tc.createTicket.conversationReq': '대화 *',
    'tc.createTicket.titleReq': '티켓 제목 *',
    'tc.createTicket.descriptionReq': '설명 *',
    'tc.createTicket.loadingCustomers': '고객을 불러오는 중…',
    'tc.createTicket.selectCustomer': '고객 선택',
    'tc.createTicket.selectCustomerFirst': '먼저 고객을 선택하세요',
    'tc.createTicket.loadingConversations': '대화를 불러오는 중…',
    'tc.createTicket.noConversations': '이 고객의 대화가 없습니다',
    'tc.createTicket.selectConversation': '대화 선택',
    'tc.createTicket.titlePlaceholder': '문제에 대한 간단한 설명',
    'tc.createTicket.descriptionPlaceholder': '문제 또는 요청에 대한 자세한 설명',
    'tc.createTicket.priority': '우선순위',
    'tc.createTicket.category': '카테고리',
    'tc.createTicket.create': '티켓 만들기',
    'tc.createTicket.creating': '만드는 중…',
    'tc.priority.low': '낮음',
    'tc.priority.medium': '보통',
    'tc.priority.high': '높음',
    'tc.priority.urgent': '긴급',
    'tc.category.general': '일반 지원',
    'tc.category.technical': '기술 문제',
    'tc.category.billing': '결제',
    'tc.category.feature': '기능 요청',
    'tc.newConv.title': '새 대화 시작',
    'tc.newConv.desc': '고객 정보를 입력하여 새 채팅 대화를 시작하세요',
    'tc.newConv.nameReq': '고객 이름 *',
    'tc.newConv.namePlaceholder': '고객 이름 입력',
    'tc.newConv.phoneOptional': '전화번호(선택 사항)',
    'tc.newConv.startChat': '채팅 시작',
    'tc.toast.noStaffOnline.title': '온라인 직원 없음',
    'tc.toast.noStaffOnline.desc': '현재 온라인 상태인 다른 직원이 없습니다.',
    'tc.teamMembers': '팀원',
    'tc.refreshStaffPresence': '직원 및 접속 상태 새로 고침',
    'tc.loadingStaff': '직원을 불러오는 중…',
    'tc.loadStaffError': '직원을 불러올 수 없습니다. 새로 고침을 눌러 다시 시도하세요.',
    'tc.thisIsYou': '본인입니다',
    'tc.chat': '채팅',
    'tc.chatUnread': '채팅 · 읽지 않음 {count}개',
    'tc.presenceReconnecting': '실시간 접속 상태를 다시 연결하는 중… 온라인/오프라인 상태가 일시적으로 오래된 것일 수 있습니다.',
    'tc.videoCallInProgress': '영상 통화 진행 중...',
    'tc.voiceCallInProgress': '음성 통화 진행 중...',
    'tc.chatModal.privateDesc': '비공개 직원 채팅 — 본인과 {name}만 이 메시지를 볼 수 있습니다.',
    'tc.chatModal.opening': '채팅을 여는 중…',
    'tc.chatModal.noMessages': '아직 메시지가 없습니다. 인사를 건네보세요.',
    'tc.chatModal.inputPlaceholder': '메시지 입력…',
    'tc.chatModal.sending': '전송 중…',
    'tc.chatModal.failed': '실패 ·',
  },
  zh: {
    'tc.quickCall.desc': '选择您想要的通话方式',
    'tc.tab.dialpad': '拨号盘',
    'tc.tab.recent': '最近',
    'tc.tab.contacts': '联系人',
    'tc.tab.activeComm': '活动通信',
    'tc.tab.analytics': '分析',
    'tc.dialpad.phonePlaceholder': '输入员工电话号码',
    'tc.dialpad.help': '仅限员工之间 — 通过电话拨打租户员工。不支持外部（PSTN）拨号。',
    'tc.clear': '清除',
    'tc.call': '呼叫',
    'tc.noRecentCalls': '尚无最近的员工通话。',
    'tc.noOtherStaffOnline': '当前没有其他员工在线。',
    'tc.voiceCall': '语音通话',
    'tc.videoCall': '视频通话',
    'tc.selectedLabel': '已选择：{name}',
    'tc.stat.totalCalls': '通话总数',
    'tc.stat.successful': '成功',
    'tc.stat.successRate': '成功率',
    'tc.stat.onlineNow': '当前在线',
    'tc.stat.completedCalls': '已完成通话',
    'tc.teamCallHistory': '团队通话记录',
    'tc.myCallHistory': '我的通话记录',
    'tc.history.supervisorDesc': '您正在查看整个团队的通话记录（监督访问权限）。',
    'tc.history.ownDesc': '您只查看自己的通话记录。',
    'tc.history.loading': '正在加载通话记录...',
    'tc.history.empty': '暂无通话记录',
    'tc.history.emptyHint': '开始通话后，通话将显示在此处',
    'tc.quickActions': '快捷操作',
    'tc.startVoiceCall': '开始语音通话',
    'tc.startVideoCall': '开始视频通话',
    'tc.startCustomerConversation': '开始客户会话',
    'tc.startAudioConference': '开始音频会议',
    'tc.conf.inProgress': '会议进行中',
    'tc.conf.startTitle': '与在线团队成员开始音频会议',
    'tc.conf.inProgressTitle': '已有会议正在进行中',
    'tc.conf.manageHint': '在会议面板中管理参与者。',
    'tc.conf.audioOnlyHint': '仅音频。已邀请在线团队成员；可从面板添加更多成员。',
    'tc.refreshStatistics': '刷新统计',
    'tc.refreshing': '正在刷新…',
    'tc.lastRefreshed': '上次刷新',
    'tc.callPerformance': '通话表现',
    'tc.analytics.empty': '暂无分析数据',
    'tc.connectionStatus': '连接状态',
    'tc.webrtcStatus': 'WebRTC 状态',
    'tc.onlineUsers': '在线用户',
    'tc.currentCall': '当前通话',
    'tc.teamConv.title': '开始团队会话',
    'tc.teamConv.desc': '选择团队成员并撰写消息以开始群组会话',
    'tc.teamConv.selectMembers': '选择团队成员',
    'tc.teamConv.membersSelected': '已选择 {count} 名成员',
    'tc.teamConv.message': '消息',
    'tc.teamConv.messagePlaceholder': '输入给团队的消息...',
    'tc.startConversation': '开始会话',
    'tc.noStaffYet': '此企业尚无员工。',
    'tc.you': '（您）',
    'tc.createTicket.title': '创建客户支持工单',
    'tc.createTicket.desc': '选择一位客户及其一次会话，然后描述问题。',
    'tc.createTicket.customerReq': '客户 *',
    'tc.createTicket.conversationReq': '会话 *',
    'tc.createTicket.titleReq': '工单标题 *',
    'tc.createTicket.descriptionReq': '描述 *',
    'tc.createTicket.loadingCustomers': '正在加载客户…',
    'tc.createTicket.selectCustomer': '选择客户',
    'tc.createTicket.selectCustomerFirst': '请先选择客户',
    'tc.createTicket.loadingConversations': '正在加载会话…',
    'tc.createTicket.noConversations': '此客户没有会话',
    'tc.createTicket.selectConversation': '选择会话',
    'tc.createTicket.titlePlaceholder': '问题的简要描述',
    'tc.createTicket.descriptionPlaceholder': '问题或请求的详细描述',
    'tc.createTicket.priority': '优先级',
    'tc.createTicket.category': '类别',
    'tc.createTicket.create': '创建工单',
    'tc.createTicket.creating': '正在创建…',
    'tc.priority.low': '低',
    'tc.priority.medium': '中',
    'tc.priority.high': '高',
    'tc.priority.urgent': '紧急',
    'tc.category.general': '常规支持',
    'tc.category.technical': '技术问题',
    'tc.category.billing': '账单',
    'tc.category.feature': '功能请求',
    'tc.newConv.title': '开始新会话',
    'tc.newConv.desc': '输入客户详细信息以开始新的聊天会话',
    'tc.newConv.nameReq': '客户姓名 *',
    'tc.newConv.namePlaceholder': '输入客户姓名',
    'tc.newConv.phoneOptional': '电话号码（可选）',
    'tc.newConv.startChat': '开始聊天',
    'tc.toast.noStaffOnline.title': '没有员工在线',
    'tc.toast.noStaffOnline.desc': '当前没有其他员工在线。',
    'tc.teamMembers': '团队成员',
    'tc.refreshStaffPresence': '刷新员工和在线状态',
    'tc.loadingStaff': '正在加载员工…',
    'tc.loadStaffError': '无法加载员工。点击刷新重试。',
    'tc.thisIsYou': '这是您',
    'tc.chat': '聊天',
    'tc.chatUnread': '聊天 · {count} 条未读',
    'tc.presenceReconnecting': '正在重新连接实时在线状态… 在线/离线状态可能暂时过时。',
    'tc.videoCallInProgress': '视频通话进行中...',
    'tc.voiceCallInProgress': '语音通话进行中...',
    'tc.chatModal.privateDesc': '私密员工聊天 — 只有您和 {name} 可以看到这些消息。',
    'tc.chatModal.opening': '正在打开聊天…',
    'tc.chatModal.noMessages': '暂无消息。打个招呼吧。',
    'tc.chatModal.inputPlaceholder': '输入消息…',
    'tc.chatModal.sending': '正在发送…',
    'tc.chatModal.failed': '失败 ·',
  },
  ar: {
    'tc.quickCall.desc': 'اختر كيف تريد إجراء مكالمتك',
    'tc.tab.dialpad': 'لوحة الاتصال',
    'tc.tab.recent': 'الأخيرة',
    'tc.tab.contacts': 'جهات الاتصال',
    'tc.tab.activeComm': 'الاتصال النشط',
    'tc.tab.analytics': 'التحليلات',
    'tc.dialpad.phonePlaceholder': 'أدخل رقم هاتف الموظف',
    'tc.dialpad.help': 'بين الموظفين فقط — يتصل بموظف المستأجر عبر الهاتف. الاتصال الخارجي (PSTN) غير مدعوم.',
    'tc.clear': 'مسح',
    'tc.call': 'اتصال',
    'tc.noRecentCalls': 'لا توجد مكالمات موظفين حديثة بعد.',
    'tc.noOtherStaffOnline': 'لا يوجد موظفون آخرون متصلون حاليًا.',
    'tc.voiceCall': 'مكالمة صوتية',
    'tc.videoCall': 'مكالمة فيديو',
    'tc.selectedLabel': 'محدد: {name}',
    'tc.stat.totalCalls': 'إجمالي المكالمات',
    'tc.stat.successful': 'ناجحة',
    'tc.stat.successRate': 'معدل النجاح',
    'tc.stat.onlineNow': 'متصل الآن',
    'tc.stat.completedCalls': 'المكالمات المكتملة',
    'tc.teamCallHistory': 'سجل مكالمات الفريق',
    'tc.myCallHistory': 'سجل مكالماتي',
    'tc.history.supervisorDesc': 'أنت تعرض سجل المكالمات لفريقك بالكامل (وصول إشرافي).',
    'tc.history.ownDesc': 'أنت تعرض سجل مكالماتك الخاص فقط.',
    'tc.history.loading': 'جارٍ تحميل سجل المكالمات...',
    'tc.history.empty': 'لا يوجد سجل مكالمات متاح',
    'tc.history.emptyHint': 'ستظهر المكالمات هنا بمجرد أن تبدأ في إجرائها',
    'tc.quickActions': 'إجراءات سريعة',
    'tc.startVoiceCall': 'بدء مكالمة صوتية',
    'tc.startVideoCall': 'بدء مكالمة فيديو',
    'tc.startCustomerConversation': 'بدء محادثة مع العميل',
    'tc.startAudioConference': 'بدء مؤتمر صوتي',
    'tc.conf.inProgress': 'المؤتمر جارٍ',
    'tc.conf.startTitle': 'بدء مؤتمر صوتي مع أعضاء الفريق المتصلين',
    'tc.conf.inProgressTitle': 'يوجد مؤتمر قيد التقدم بالفعل',
    'tc.conf.manageHint': 'إدارة المشاركين في لوحة المؤتمر.',
    'tc.conf.audioOnlyHint': 'صوت فقط. تتم دعوة الزملاء المتصلين؛ أضف المزيد من اللوحة.',
    'tc.refreshStatistics': 'تحديث الإحصائيات',
    'tc.refreshing': 'جارٍ التحديث…',
    'tc.lastRefreshed': 'آخر تحديث',
    'tc.callPerformance': 'أداء المكالمات',
    'tc.analytics.empty': 'لا توجد بيانات تحليلية متاحة',
    'tc.connectionStatus': 'حالة الاتصال',
    'tc.webrtcStatus': 'حالة WebRTC',
    'tc.onlineUsers': 'المستخدمون المتصلون',
    'tc.currentCall': 'المكالمة الحالية',
    'tc.teamConv.title': 'بدء محادثة الفريق',
    'tc.teamConv.desc': 'حدد أعضاء الفريق وأنشئ رسالة لبدء محادثة جماعية',
    'tc.teamConv.selectMembers': 'تحديد أعضاء الفريق',
    'tc.teamConv.membersSelected': 'تم تحديد {count} عضو',
    'tc.teamConv.message': 'الرسالة',
    'tc.teamConv.messagePlaceholder': 'اكتب رسالتك إلى الفريق...',
    'tc.startConversation': 'بدء المحادثة',
    'tc.noStaffYet': 'لا يوجد موظفون في هذه الشركة بعد.',
    'tc.you': '(أنت)',
    'tc.createTicket.title': 'إنشاء تذكرة دعم العملاء',
    'tc.createTicket.desc': 'حدد عميلاً وإحدى محادثاته، ثم صف المشكلة.',
    'tc.createTicket.customerReq': 'العميل *',
    'tc.createTicket.conversationReq': 'المحادثة *',
    'tc.createTicket.titleReq': 'عنوان التذكرة *',
    'tc.createTicket.descriptionReq': 'الوصف *',
    'tc.createTicket.loadingCustomers': 'جارٍ تحميل العملاء…',
    'tc.createTicket.selectCustomer': 'حدد عميلاً',
    'tc.createTicket.selectCustomerFirst': 'حدد عميلاً أولاً',
    'tc.createTicket.loadingConversations': 'جارٍ تحميل المحادثات…',
    'tc.createTicket.noConversations': 'لا توجد محادثات لهذا العميل',
    'tc.createTicket.selectConversation': 'حدد محادثة',
    'tc.createTicket.titlePlaceholder': 'وصف موجز للمشكلة',
    'tc.createTicket.descriptionPlaceholder': 'وصف تفصيلي للمشكلة أو الطلب',
    'tc.createTicket.priority': 'الأولوية',
    'tc.createTicket.category': 'الفئة',
    'tc.createTicket.create': 'إنشاء تذكرة',
    'tc.createTicket.creating': 'جارٍ الإنشاء…',
    'tc.priority.low': 'منخفضة',
    'tc.priority.medium': 'متوسطة',
    'tc.priority.high': 'عالية',
    'tc.priority.urgent': 'عاجلة',
    'tc.category.general': 'الدعم العام',
    'tc.category.technical': 'مشكلة تقنية',
    'tc.category.billing': 'الفوترة',
    'tc.category.feature': 'طلب ميزة',
    'tc.newConv.title': 'بدء محادثة جديدة',
    'tc.newConv.desc': 'أدخل بيانات العميل لبدء محادثة دردشة جديدة',
    'tc.newConv.nameReq': 'اسم العميل *',
    'tc.newConv.namePlaceholder': 'أدخل اسم العميل',
    'tc.newConv.phoneOptional': 'رقم الهاتف (اختياري)',
    'tc.newConv.startChat': 'بدء الدردشة',
    'tc.toast.noStaffOnline.title': 'لا يوجد موظفون متصلون',
    'tc.toast.noStaffOnline.desc': 'لا يوجد موظفون آخرون متصلون حاليًا.',
    'tc.teamMembers': 'أعضاء الفريق',
    'tc.refreshStaffPresence': 'تحديث الموظفين والتواجد',
    'tc.loadingStaff': 'جارٍ تحميل الموظفين…',
    'tc.loadStaffError': 'تعذر تحميل الموظفين. اضغط على تحديث لإعادة المحاولة.',
    'tc.thisIsYou': 'هذا أنت',
    'tc.chat': 'دردشة',
    'tc.chatUnread': 'دردشة · {count} غير مقروءة',
    'tc.presenceReconnecting': 'إعادة الاتصال بالتواجد المباشر… قد تكون حالة الاتصال/عدم الاتصال قديمة مؤقتًا.',
    'tc.videoCallInProgress': 'مكالمة فيديو قيد التقدم...',
    'tc.voiceCallInProgress': 'مكالمة صوتية قيد التقدم...',
    'tc.chatModal.privateDesc': 'دردشة خاصة بين الموظفين — يمكنك أنت و{name} فقط رؤية هذه الرسائل.',
    'tc.chatModal.opening': 'جارٍ فتح الدردشة…',
    'tc.chatModal.noMessages': 'لا توجد رسائل بعد. ألقِ التحية.',
    'tc.chatModal.inputPlaceholder': 'اكتب رسالة…',
    'tc.chatModal.sending': 'جارٍ الإرسال…',
    'tc.chatModal.failed': 'فشل ·',
  },
};

export default teamCollabTranslations;
